import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import Link from '@components/atoms/link/link';
import { ApiPricingBreakdown } from '@interfaces/pricingBreakdown';
import Icon from '@components/atoms/icon/icon';
import { mapToMinimumPricingBreakdown } from '@maps/pricingBreakdown/pricingBreakdown';
import PriceBreakDownTooltip from '../price-breakdown-tooltip/price-breakdown-tooltip';
import styles from './negotiation-modal-product.module.scss';

type NegotiationModalProductInfoProps = {
  picturePath: string;
  brandName: string;
  description: string;
  price: string;
  isSeller: boolean;
  productPath?: string;
  onClose?: () => void;
  alt?: string;
  breakdown?: ApiPricingBreakdown;
};

type NegotiationModalProductPriceProps = Pick<NegotiationModalProductInfoProps, 'price' | 'isSeller' | 'breakdown'>;

const NegotiationModalProductPrice: FC<NegotiationModalProductPriceProps> = ({ price, breakdown, isSeller }) => {
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');

  const minPriceBreakdown = mapToMinimumPricingBreakdown(breakdown);

  if (isSeller) {
    return (
      <>
        <span data-cy="mmao_product_price">{`${t('BUYERFEE.INFOMODAL.PRICE')}: ${price}`}</span>{' '}
        <p className={styles.mmao__product__text__buyerFeeText}>
          {t('BUYERFEE.DISCLAIMER.EXCLUDED.SELLER')}
          <PriceBreakDownTooltip
            breakdown={minPriceBreakdown}
            isSeller={isSeller}
          >
            <Icon
              className={styles.mmao__product__text__buyerFeeText__icon}
              name="help-default"
              aria-label={tA11y('A11Y.ICON.HELP_CENTER')}
            />
          </PriceBreakDownTooltip>
        </p>
      </>
    );
  }

  return (
    <span data-cy="mmao_product_price">
      {`${t('BUYERFEE.INFOMODAL.PRICE')}: ${price}`}
      <PriceBreakDownTooltip
        breakdown={minPriceBreakdown}
        isSeller={isSeller}
      >
        <Icon
          name="infobox-default"
          className={styles.mmao__product__text__buyerFeeText__icon}
          ariaLabel={tA11y('A11Y.ICON.INFO_PRICE_DETAILS')}
        />
      </PriceBreakDownTooltip>
    </span>
  );
};

export const NegotiationModalProductInfo: FC<NegotiationModalProductInfoProps> = ({
  picturePath,
  productPath = '',
  brandName,
  description,
  price,
  isSeller,
  onClose,
  alt,
  breakdown,
}) => {
  return (
    <div className={styles.mmao__product}>
      <Link
        href={productPath}
        onClick={onClose}
      >
        <img
          className={styles.mmao__product__picture}
          src={picturePath}
          alt={alt}
          data-cy="mmao_product_img"
        />
      </Link>
      <div
        className={styles.mmao__product__text}
        data-cy="mmao_product_description"
      >
        <p className={styles.mmao__product__text__brandName}>{brandName}</p>
        <p className={styles.mmao__product__text__description}>{description}</p>
        <p className={styles.mmao__product__text__price}>
          <NegotiationModalProductPrice
            price={price}
            breakdown={breakdown}
            isSeller={isSeller}
          />
        </p>
      </div>
    </div>
  );
};
